
import { Record, FSharpRef, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { string_type, bool_type, record_type, class_type, bigint_type, union_type, int32_type, uint32_type, array_type, option_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { Memory1, InitMemData_$reflection } from "../Common/CommonTypes.js";
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { copy, setItem, item, fill as fill_1, copyTo } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { compare, equals, op_BitwiseAnd, fromOne, op_LeftShift, op_Subtraction, fromUInt32, toUInt32, fromZero } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { iterate, empty as empty_1, filter as filter_1, count as count_3, toSeq, add, tryFind } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { Exception } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { toList, filter, truncate, length } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { sortBy, cons, empty, length as length_1 } from "../fable_modules/fable-library-js.5.13.0/List.js";

export const Constants_maxDenseAddressWidth = 16;

export const Constants_maxTrieAddressWidth = 32;

export const Constants_maxSlotsForWholeRead = 4000;

export const Constants_trieBranch = 16;

export const Constants_minTailBeforeCompact = 4096;

/**
 * A 16-way trie over the address, used when the address space is too large to give every
 * address a slot number in a flat array.
 * 
 * It is grown in place and never copied: a slot is filled once and never repointed, which is
 * what removes the per-write path copy the `Map` paid. A `Leaf` may sit above its natural depth
 * - that path compression is the whole reason this is a union rather than a fixed-depth array
 * of arrays, since depth follows from the address width and would otherwise be known - so the
 * leaf carries its own address and it is checked on arrival.
 */
export class NodeOrLeaf extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Node", "Leaf"];
    }
}

export function NodeOrLeaf_$reflection() {
    return union_type("RamStore.NodeOrLeaf", [], NodeOrLeaf, () => [[["Item", array_type(option_type(NodeOrLeaf_$reflection()))]], [["addr", uint32_type], ["slot", int32_type]]]);
}

/**
 * How an address is turned into its slot number. This is the *only* place the strategies differ:
 * everything after it works on slot numbers.
 */
export class Addressing extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Dense", "Sparse", "Wide", "Fixed"];
    }
    static Fixed = new Addressing(3, []);
}

export function Addressing_$reflection() {
    return union_type("RamStore.Addressing", [], Addressing, () => [[["Item", array_type(int32_type)]], [["root", array_type(option_type(NodeOrLeaf_$reflection()))], ["depth", int32_type]], [["Item", record_type("Microsoft.FSharp.Core.FSharpRef`1", [class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, int32_type])], FSharpRef, () => [["contents", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, int32_type])]])]], []]);
}

/**
 * A read/write memory as the simulator holds it.
 */
export class Ram extends Record {
    constructor(AddressWidth, WordWidth, BigVals, Init, Comments, InitialData, Addressing, Window$, Words, BigWords, SlotCount, SlotAddr, SlotAddrBig, CurVal, CurBig, Start, IStep, IVal, IBig, ILen, TSlot, TStep, TVal, TBig, TLen, LastStep, Writes) {
        super();
        this.AddressWidth = (AddressWidth | 0);
        this.WordWidth = (WordWidth | 0);
        this.BigVals = BigVals;
        this.Init = Init;
        this.Comments = Comments;
        this.InitialData = InitialData;
        this.Addressing = Addressing;
        this.Window = (Window$ | 0);
        this.Words = Words;
        this.BigWords = BigWords;
        this.SlotCount = (SlotCount | 0);
        this.SlotAddr = SlotAddr;
        this.SlotAddrBig = SlotAddrBig;
        this.CurVal = CurVal;
        this.CurBig = CurBig;
        this.Start = Start;
        this.IStep = IStep;
        this.IVal = IVal;
        this.IBig = IBig;
        this.ILen = (ILen | 0);
        this.TSlot = TSlot;
        this.TStep = TStep;
        this.TVal = TVal;
        this.TBig = TBig;
        this.TLen = (TLen | 0);
        this.LastStep = (LastStep | 0);
        this.Writes = (Writes | 0);
    }
}

export function Ram_$reflection() {
    return record_type("RamStore.Ram", [], Ram, () => [["AddressWidth", int32_type], ["WordWidth", int32_type], ["BigVals", bool_type], ["Init", InitMemData_$reflection()], ["Comments", option_type(class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, string_type]))], ["InitialData", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [bigint_type, bigint_type])], ["Addressing", Addressing_$reflection()], ["Window", int32_type], ["Words", array_type(uint32_type)], ["BigWords", array_type(bigint_type)], ["SlotCount", int32_type], ["SlotAddr", array_type(uint32_type)], ["SlotAddrBig", array_type(bigint_type)], ["CurVal", array_type(uint32_type)], ["CurBig", array_type(bigint_type)], ["Start", array_type(int32_type)], ["IStep", array_type(int32_type)], ["IVal", array_type(uint32_type)], ["IBig", array_type(bigint_type)], ["ILen", int32_type], ["TSlot", array_type(int32_type)], ["TStep", array_type(int32_type)], ["TVal", array_type(uint32_type)], ["TBig", array_type(bigint_type)], ["TLen", int32_type], ["LastStep", int32_type], ["Writes", int32_type]]);
}

function ensureInt(arr, n) {
    if (arr.length >= n) {
        return arr;
    }
    else {
        const a = new Int32Array(max(8, max(n, arr.length * 2)));
        copyTo(arr, 0, a, 0, arr.length);
        return a;
    }
}

function ensureU32(arr, n) {
    if (arr.length >= n) {
        return arr;
    }
    else {
        const a = new Uint32Array(max(8, max(n, arr.length * 2)));
        copyTo(arr, 0, a, 0, arr.length);
        return a;
    }
}

function ensureBig(arr, n) {
    if (arr.length >= n) {
        return arr;
    }
    else {
        const a = fill_1(new Array(max(8, max(n, arr.length * 2))), 0, max(8, max(n, arr.length * 2)), fromZero());
        copyTo(arr, 0, a, 0, arr.length);
        return a;
    }
}

function lastAtOrBefore(steps, lo, hi, step) {
    let a = lo;
    let b = hi - 1;
    let ans = -1;
    while (a <= b) {
        const mid = ~~((a + b) / 2) | 0;
        if (item(mid, steps) <= step) {
            ans = (mid | 0);
            a = ((mid + 1) | 0);
        }
        else {
            b = ((mid - 1) | 0);
        }
    }
    return ans | 0;
}

function nibble(addr, level, depth) {
    return ~~(((addr >>> (4 * ((depth - 1) - level))) & 15) >>> 0) | 0;
}

function findAt(arr_mut, level_mut, addr_mut, depth_mut) {
    findAt:
    while (true) {
        const arr = arr_mut, level = level_mut, addr = addr_mut, depth = depth_mut;
        const matchValue = item(nibble(addr, level, depth), arr);
        if (matchValue != null) {
            if (matchValue.tag === 0) {
                const children = matchValue.fields[0];
                arr_mut = children;
                level_mut = (level + 1);
                addr_mut = addr;
                depth_mut = depth;
                continue findAt;
            }
            else {
                const a = matchValue.fields[0];
                const slot = matchValue.fields[1] | 0;
                if (a === addr) {
                    return slot | 0;
                }
                else {
                    return -1;
                }
            }
        }
        else {
            return -1;
        }
        break;
    }
}

function insertAt(arr_mut, level_mut, addr_mut, depth_mut, mk_mut) {
    insertAt:
    while (true) {
        const arr = arr_mut, level = level_mut, addr = addr_mut, depth = depth_mut, mk = mk_mut;
        const i = nibble(addr, level, depth) | 0;
        const matchValue = item(i, arr);
        if (matchValue != null) {
            if (matchValue.tag === 0) {
                const children_1 = matchValue.fields[0];
                arr_mut = children_1;
                level_mut = (level + 1);
                addr_mut = addr;
                depth_mut = depth;
                mk_mut = mk;
                continue insertAt;
            }
            else if ((matchValue.fields[1], matchValue.fields[0] === addr)) {
                const a_1 = matchValue.fields[0];
                const slot_2 = matchValue.fields[1] | 0;
                return slot_2 | 0;
            }
            else {
                const a_2 = matchValue.fields[0];
                const slot_3 = matchValue.fields[1] | 0;
                const children = fill_1(new Array(Constants_trieBranch), 0, Constants_trieBranch, undefined);
                setItem(children, nibble(a_2, level + 1, depth), new NodeOrLeaf(/* Leaf */ 1, [a_2, slot_3]));
                setItem(arr, i, new NodeOrLeaf(/* Node */ 0, [children]));
                arr_mut = children;
                level_mut = (level + 1);
                addr_mut = addr;
                depth_mut = depth;
                mk_mut = mk;
                continue insertAt;
            }
        }
        else {
            const slot_1 = mk() | 0;
            setItem(arr, i, new NodeOrLeaf(/* Leaf */ 1, [addr, slot_1]));
            return slot_1 | 0;
        }
        break;
    }
}

function slotOf(ram, addr) {
    const matchValue = ram.Addressing;
    switch (matchValue.tag) {
        case 1:
            return findAt(matchValue.fields[0], 0, addr, matchValue.fields[1]) | 0;
        case 2:
        case 3:
            return -1;
        default:
            return item(~~addr, matchValue.fields[0]) | 0;
    }
}

function slotOfBig(ram, addr) {
    const matchValue = ram.Addressing;
    if (matchValue.tag === 2) {
        const matchValue_1 = tryFind(addr, matchValue.fields[0].contents);
        if (matchValue_1 == null) {
            return -1;
        }
        else {
            return matchValue_1 | 0;
        }
    }
    else {
        return slotOf(ram, toUInt32(addr) >>> 0) | 0;
    }
}

function newSlot(ram, addr, addrBig) {
    const slot = ram.SlotCount | 0;
    ram.SlotCount = ((slot + 1) | 0);
    ram.SlotAddr = ensureU32(ram.SlotAddr, slot + 1);
    ram.CurVal = ensureU32(ram.CurVal, slot + 1);
    if (ram.BigVals) {
        ram.CurBig = ensureBig(ram.CurBig, slot + 1);
    }
    ram.SlotAddr[slot] = addr;
    if (ram.Addressing.tag === 2) {
        void (ram.SlotAddrBig.push(addrBig));
    }
    return slot | 0;
}

function slotFor(ram, addr) {
    const matchValue = ram.Addressing;
    switch (matchValue.tag) {
        case 1:
            return insertAt(matchValue.fields[0], 0, addr, matchValue.fields[1], () => (newSlot(ram, addr, fromUInt32(addr)) | 0)) | 0;
        case 2:
            throw new Exception("RamStore: a wide-addressed memory must be written through slotForBig");
        case 3:
            throw new Exception("RamStore: a read-only memory cannot be written");
        default: {
            const slots = matchValue.fields[0];
            const s = item(~~addr, slots) | 0;
            if (s >= 0) {
                return s | 0;
            }
            else {
                const s_1 = newSlot(ram, addr, fromUInt32(addr)) | 0;
                setItem(slots, ~~addr, s_1 | 0);
                return s_1 | 0;
            }
        }
    }
}

function slotForBig(ram, addr) {
    const matchValue = ram.Addressing;
    if (matchValue.tag === 2) {
        const table = matchValue.fields[0];
        const matchValue_1 = tryFind(addr, table.contents);
        if (matchValue_1 == null) {
            const slot_1 = newSlot(ram, 0, addr) | 0;
            table.contents = add(addr, slot_1, table.contents);
            return slot_1 | 0;
        }
        else {
            return matchValue_1 | 0;
        }
    }
    else {
        return slotFor(ram, toUInt32(addr) >>> 0) | 0;
    }
}

function addressOf(ram, slot) {
    if (ram.Addressing.tag === 2) {
        return item(slot, ram.SlotAddrBig);
    }
    else {
        return fromUInt32(item(slot, ram.SlotAddr));
    }
}

function compact(ram) {
    const slotCount = ram.SlotCount | 0;
    const lowWater = (ram.LastStep - ram.Window) | 0;
    const tCount = new Int32Array(slotCount + 1);
    for (let i = 0; i <= (ram.TLen - 1); i++) {
        setItem(tCount, item(i, ram.TSlot), (item(item(i, ram.TSlot), tCount) + 1) | 0);
    }
    const tStart = new Int32Array(slotCount + 1);
    let acc = 0;
    for (let s = 0; s <= (slotCount - 1); s++) {
        setItem(tStart, s, acc | 0);
        acc = ((acc + item(s, tCount)) | 0);
    }
    setItem(tStart, slotCount, acc | 0);
    const fill = copy(tStart);
    const tOrder = new Int32Array(ram.TLen);
    for (let i_1 = 0; i_1 <= (ram.TLen - 1); i_1++) {
        const s_1 = item(i_1, ram.TSlot) | 0;
        setItem(tOrder, item(s_1, fill), i_1 | 0);
        setItem(fill, s_1, (item(s_1, fill) + 1) | 0);
    }
    const oldStart = ram.Start;
    const oldStartValid = oldStart.length | 0;
    const dropCount = (s_2) => {
        if ((ram.Window <= 0) ? true : (lowWater <= 0)) {
            return 0;
        }
        else {
            let iLo;
            const s_3 = s_2 | 0;
            iLo = (((s_3 + 1) < oldStartValid) ? item(s_3, oldStart) : ram.ILen);
            let iHi;
            const s_4 = s_2 | 0;
            iHi = (((s_4 + 1) < oldStartValid) ? item(s_4 + 1, oldStart) : ram.ILen);
            let nLE = 0;
            let k = iLo;
            while ((k < iHi) && (item(k, ram.IStep) <= lowWater)) {
                nLE = ((nLE + 1) | 0);
                k = ((k + 1) | 0);
            }
            if (nLE === (iHi - iLo)) {
                let j = item(s_2, tStart);
                while ((j < item(s_2 + 1, tStart)) && (item(item(j, tOrder), ram.TStep) <= lowWater)) {
                    nLE = ((nLE + 1) | 0);
                    j = ((j + 1) | 0);
                }
            }
            return max(0, nLE - 1) | 0;
        }
    };
    const newStart = new Int32Array(slotCount + 1);
    let out = 0;
    for (let s_5 = 0; s_5 <= (slotCount - 1); s_5++) {
        let s_6, s_7;
        setItem(newStart, s_5, out | 0);
        const total = ((((s_6 = (s_5 | 0), ((s_6 + 1) < oldStartValid) ? item(s_6 + 1, oldStart) : ram.ILen)) - ((s_7 = (s_5 | 0), ((s_7 + 1) < oldStartValid) ? item(s_7, oldStart) : ram.ILen))) + (item(s_5 + 1, tStart) - item(s_5, tStart))) | 0;
        out = (((out + total) - dropCount(s_5)) | 0);
    }
    setItem(newStart, slotCount, out | 0);
    const nStep = new Int32Array(max(8, out));
    const nVal = ram.BigVals ? (new Uint32Array(0)) : (new Uint32Array(max(8, out)));
    const nBig = ram.BigVals ? fill_1(new Array(max(8, out)), 0, max(8, out), fromZero()) : (new Array(0));
    for (let s_8 = 0; s_8 <= (slotCount - 1); s_8++) {
        let drop = dropCount(s_8);
        let o = item(s_8, newStart);
        let k_1;
        const s_9 = s_8 | 0;
        k_1 = (((s_9 + 1) < oldStartValid) ? item(s_9, oldStart) : ram.ILen);
        let iHi_1;
        const s_10 = s_8 | 0;
        iHi_1 = (((s_10 + 1) < oldStartValid) ? item(s_10 + 1, oldStart) : ram.ILen);
        while (k_1 < iHi_1) {
            if (drop > 0) {
                drop = ((drop - 1) | 0);
            }
            else {
                setItem(nStep, o, item(k_1, ram.IStep) | 0);
                if (ram.BigVals) {
                    setItem(nBig, o, item(k_1, ram.IBig));
                }
                else {
                    setItem(nVal, o, item(k_1, ram.IVal));
                }
                o = ((o + 1) | 0);
            }
            k_1 = ((k_1 + 1) | 0);
        }
        let j_1 = item(s_8, tStart);
        while (j_1 < item(s_8 + 1, tStart)) {
            const i_2 = item(j_1, tOrder) | 0;
            if (drop > 0) {
                drop = ((drop - 1) | 0);
            }
            else {
                setItem(nStep, o, item(i_2, ram.TStep) | 0);
                if (ram.BigVals) {
                    setItem(nBig, o, item(i_2, ram.TBig));
                }
                else {
                    setItem(nVal, o, item(i_2, ram.TVal));
                }
                o = ((o + 1) | 0);
            }
            j_1 = ((j_1 + 1) | 0);
        }
    }
    ram.Start = newStart;
    ram.IStep = nStep;
    ram.IVal = nVal;
    ram.IBig = nBig;
    ram.ILen = (out | 0);
    ram.TLen = 0;
}

function ensureCompact(ram) {
    if (ram.TLen > 0) {
        compact(ram);
    }
}

const lowWordMask = op_Subtraction(op_LeftShift(fromOne(), 32), fromOne());

function toU(v) {
    return toUInt32(op_BitwiseAnd(v, lowWordMask)) >>> 0;
}

/**
 * Current contents at `addr`, on the uint32 path. One array index in the dense case, which is
 * the case every RAM a design can actually simulate falls into.
 */
export function readU(ram, addr) {
    const matchValue = ram.Addressing;
    switch (matchValue.tag) {
        case 0:
            return item(~~addr, ram.Words);
        case 3:
            return toU(defaultArg(tryFind(fromUInt32(addr), ram.InitialData), fromZero()));
        default: {
            const matchValue_1 = slotOf(ram, addr) | 0;
            if (matchValue_1 === -1) {
                return 0;
            }
            else {
                return item(matchValue_1, ram.CurVal);
            }
        }
    }
}

/**
 * Current contents at `addr`, on the bigint-value path.
 * 
 * A store keeps values in one of two forms, chosen once by word width, and the caller has to ask
 * for the one it holds - the evaluators do, via `BigIntState`. Asking for the other would
 * otherwise read an array that was never filled, so it is redirected rather than trusted.
 */
export function readBigVal(ram, addr) {
    if (!ram.BigVals) {
        return fromUInt32(readU(ram, addr));
    }
    else {
        const matchValue = ram.Addressing;
        switch (matchValue.tag) {
            case 0:
                return item(~~addr, ram.BigWords);
            case 3:
                return defaultArg(tryFind(fromUInt32(addr), ram.InitialData), fromZero());
            default: {
                const matchValue_1 = slotOf(ram, addr) | 0;
                if (matchValue_1 === -1) {
                    return fromZero();
                }
                else {
                    return item(matchValue_1, ram.CurBig);
                }
            }
        }
    }
}

function readWideU(ram, addr) {
    const matchValue = slotOfBig(ram, addr) | 0;
    if (matchValue === -1) {
        return 0;
    }
    else {
        return item(matchValue, ram.CurVal);
    }
}

function readWideBig(ram, addr) {
    if (!ram.BigVals) {
        return fromUInt32(readWideU(ram, addr));
    }
    else {
        const matchValue = slotOfBig(ram, addr) | 0;
        if (matchValue === -1) {
            return fromZero();
        }
        else {
            return item(matchValue, ram.CurBig);
        }
    }
}

/**
 * The four combinations the evaluators need, named as the `Memory1` versions they replace so
 * that the call sites read the same.
 */
export function readAddrUInt32DataUInt32(ram, addr) {
    return readU(ram, addr);
}

export function readAddrUInt32DataBigInt(ram, addr) {
    return readBigVal(ram, addr);
}

export function readAddrBigIntDataUInt32(ram, addr) {
    if (ram.Addressing.tag === 2) {
        return readWideU(ram, addr);
    }
    else {
        return readU(ram, toUInt32(addr) >>> 0);
    }
}

export function readAddrBigIntDataBigInt(ram, addr) {
    if (ram.Addressing.tag === 2) {
        return readWideBig(ram, addr);
    }
    else {
        return readBigVal(ram, toUInt32(addr) >>> 0);
    }
}

function append(ram, slot, step, value, big) {
    const n = ram.TLen | 0;
    ram.TSlot = ensureInt(ram.TSlot, n + 1);
    ram.TStep = ensureInt(ram.TStep, n + 1);
    if (ram.BigVals) {
        ram.TBig = ensureBig(ram.TBig, n + 1);
    }
    else {
        ram.TVal = ensureU32(ram.TVal, n + 1);
    }
    ram.TSlot[n] = (slot | 0);
    ram.TStep[n] = (step | 0);
    if (ram.BigVals) {
        ram.TBig[n] = big;
    }
    else {
        ram.TVal[n] = value;
    }
    ram.TLen = ((n + 1) | 0);
    ram.LastStep = (step | 0);
    ram.Writes = ((ram.Writes + 1) | 0);
    if ((ram.TLen >= Constants_minTailBeforeCompact) && (ram.TLen >= ram.ILen)) {
        compact(ram);
    }
}

/**
 * Record a write, unless it changes nothing.
 * 
 * A write of the value already at that address is not a write: skipping it costs one comparison
 * on a value the reducer has already read (it is the old contents, which a RAM returns as its
 * output) and saves an entry, which every later search and every later compaction then does not
 * have to walk. Nothing observable changes - the contents are identical by definition, and the
 * RAM table's read/write highlighting comes from the input step arrays rather than from here.
 */
export function writeU(ram, step, addr, value) {
    if (readU(ram, addr) !== value) {
        const slot = slotFor(ram, addr) | 0;
        ram.CurVal[slot] = value;
        if (ram.Words.length > 0) {
            ram.Words[~~addr] = value;
        }
        append(ram, slot, step, value, fromZero());
    }
}

export function writeBigVal(ram, step, addr, value) {
    if (!ram.BigVals) {
        writeU(ram, step, addr, toU(value));
    }
    else if (!equals(readBigVal(ram, addr), value)) {
        const slot = slotFor(ram, addr) | 0;
        ram.CurBig[slot] = value;
        if (ram.BigWords.length > 0) {
            ram.BigWords[~~addr] = value;
        }
        append(ram, slot, step, 0, value);
    }
}

function writeWideU(ram, step, addr, value) {
    if (readWideU(ram, addr) !== value) {
        const slot = slotForBig(ram, addr) | 0;
        ram.CurVal[slot] = value;
        append(ram, slot, step, value, fromZero());
    }
}

function writeWideBig(ram, step, addr, value) {
    if (!ram.BigVals) {
        writeWideU(ram, step, addr, toU(value));
    }
    else if (!equals(readWideBig(ram, addr), value)) {
        const slot = slotForBig(ram, addr) | 0;
        ram.CurBig[slot] = value;
        append(ram, slot, step, 0, value);
    }
}

export function writeAddrUInt32DataUInt32(ram, step, addr, value) {
    writeU(ram, step, addr, value);
}

export function writeAddrUInt32DataBigInt(ram, step, addr, value) {
    writeBigVal(ram, step, addr, value);
}

export function writeAddrBigIntDataUInt32(ram, step, addr, value) {
    if (ram.Addressing.tag === 2) {
        writeWideU(ram, step, addr, value);
    }
    else {
        writeU(ram, step, toUInt32(addr) >>> 0, value);
    }
}

export function writeAddrBigIntDataBigInt(ram, step, addr, value) {
    if (ram.Addressing.tag === 2) {
        writeWideBig(ram, step, addr, value);
    }
    else {
        writeBigVal(ram, step, toUInt32(addr) >>> 0, value);
    }
}

function slotValueAt(ram, slot, step) {
    const lo = item(slot, ram.Start) | 0;
    const hi = item(slot + 1, ram.Start) | 0;
    const matchValue = lastAtOrBefore(ram.IStep, lo, hi, step) | 0;
    if (matchValue === -1) {
        return fromZero();
    }
    else {
        const i = matchValue | 0;
        if (ram.BigVals) {
            return item(i, ram.IBig);
        }
        else {
            return fromUInt32(item(i, ram.IVal));
        }
    }
}

/**
 * What one address held at the end of `step`.
 */
export function wordAt(ram, step, addr) {
    if (ram.Addressing.tag === 3) {
        return defaultArg(tryFind(addr, ram.InitialData), fromZero());
    }
    else {
        ensureCompact(ram);
        const matchValue_1 = slotOfBig(ram, addr) | 0;
        if (matchValue_1 === -1) {
            return fromZero();
        }
        else {
            return slotValueAt(ram, matchValue_1, step);
        }
    }
}

/**
 * Whether more than `limit` words were non-zero at the end of `step`.
 * 
 * This is all the RAM table needs: it chooses between listing every non-zero location and a
 * windowed display, and the exact figure never appears. Asking the cheaper question means the
 * walk stops as soon as the answer is settled, and usually it never starts - a memory that has
 * never had more than `limit` addresses written cannot have more than `limit` non-zero.
 */
export function liveCountExceeds(ram, step, limit) {
    if (ram.Addressing.tag === 3) {
        return length(truncate(limit + 1, filter((tupledArg) => !equals(tupledArg[1], fromZero()), toSeq(ram.InitialData)))) > limit;
    }
    else if (ram.SlotCount <= limit) {
        return false;
    }
    else {
        ensureCompact(ram);
        let count_2 = 0;
        let s = 0;
        while ((count_2 <= limit) && (s < ram.SlotCount)) {
            if (!equals(slotValueAt(ram, s, step), fromZero())) {
                count_2 = ((count_2 + 1) | 0);
            }
            s = ((s + 1) | 0);
        }
        return count_2 > limit;
    }
}

/**
 * The non-zero locations at the end of `step`, in address order - or None when there are more
 * than `limit` of them.
 * 
 * This is what a RAM table's sparse display is: every location worth a row, when there are few
 * enough to list. It replaces asking `liveCountExceeds` and then building the answer with
 * `toMemory`, which is the right question answered the expensive way - the count is bounded by
 * the limit but the whole-memory read is not, so a memory with 65,536 addresses written and
 * fifty non-zero now took the sparse branch and paid 80 ms for it, on every render.
 * 
 * Bounded by TWO things, and it needs both. The slot table is the set of addresses that could be
 * non-zero - a slot exists once an address has been written, and `reset` seeds the initial
 * contents as writes at step -1 - so the walk stops at the first `limit + 1` non-zero words. But
 * a memory with 65,536 addresses written and fifty non-zero now would run out of SLOTS before it
 * ran out of limit, and walking all of them is the 80 ms read this exists to avoid. So a memory
 * with more than `Constants.maxSlotsForWholeRead` addresses ever written answers None without
 * walking at all: the caller shows a window instead.
 * 
 * The count that decides it is therefore addresses EVER WRITTEN, not words currently non-zero.
 * It has to be: the second is what the walk is for, so it cannot also be its precondition.
 */
export function sparseUpTo(ram, step, limit) {
    if (ram.Addressing.tag === 3) {
        const found = toList(truncate(limit + 1, filter((tupledArg) => !equals(tupledArg[1], fromZero()), toSeq(ram.InitialData))));
        if (length_1(found) > limit) {
            return undefined;
        }
        else {
            return found;
        }
    }
    else if (ram.SlotCount > Constants_maxSlotsForWholeRead) {
        return undefined;
    }
    else {
        ensureCompact(ram);
        let found_1 = empty();
        let count_1 = 0;
        let s = 0;
        while ((count_1 <= limit) && (s < ram.SlotCount)) {
            const v_1 = slotValueAt(ram, s, step);
            if (!equals(v_1, fromZero())) {
                count_1 = ((count_1 + 1) | 0);
                if (count_1 <= limit) {
                    found_1 = cons([addressOf(ram, s), v_1], found_1);
                }
            }
            s = ((s + 1) | 0);
        }
        if (count_1 > limit) {
            return undefined;
        }
        else {
            return sortBy((tuple) => tuple[0], found_1, {
                Compare: (x, y) => (compare(x, y) | 0),
            });
        }
    }
}

/**
 * How many words were non-zero at the end of `step`. Only diagnostics and tests want the exact
 * number; the display wants `liveCountExceeds`.
 */
export function liveCountAt(ram, step) {
    if (ram.Addressing.tag === 3) {
        return count_3(filter_1((_arg, v) => !equals(v, fromZero()), ram.InitialData)) | 0;
    }
    else {
        ensureCompact(ram);
        let count = 0;
        for (let s = 0; s <= (ram.SlotCount - 1); s++) {
            if (!equals(slotValueAt(ram, s, step), fromZero())) {
                count = ((count + 1) | 0);
            }
        }
        return count | 0;
    }
}

/**
 * The whole memory as of `step`, as the immutable type the rest of Issie uses. Only for the
 * places that genuinely want all of it - a memory diff, a golden file - never per render.
 */
export function toMemory(ram, step) {
    let acc;
    return new Memory1(ram.Init, ram.AddressWidth, ram.WordWidth, (ram.Addressing.tag === 3) ? filter_1((_arg, v) => !equals(v, fromZero()), ram.InitialData) : ((ensureCompact(ram), (acc = empty_1({
        Compare: (x, y) => (compare(x, y) | 0),
    }), ((() => {
        for (let s = 0; s <= (ram.SlotCount - 1); s++) {
            const v_1 = slotValueAt(ram, s, step);
            if (!equals(v_1, fromZero())) {
                acc = add(addressOf(ram, s), v_1, acc);
            }
        }
    })(), acc)))), ram.Comments);
}

/**
 * The whole memory as of `step`, when reading it is affordable - None when it is not.
 * 
 * What "affordable" means, and why it is a count of addresses ever written rather than of words
 * held, is on `Constants.maxSlotsForWholeRead`. A caller told None must show something bounded
 * instead, and must not fall back to `toMemory`.
 */
export function toMemoryIfSmall(ram, step) {
    if (ram.Addressing.tag === 3) {
        return toMemory(ram, step);
    }
    else if (ram.SlotCount > Constants_maxSlotsForWholeRead) {
        return undefined;
    }
    else {
        return toMemory(ram, step);
    }
}

/**
 * Put the memory back to the contents it was built with, discarding all history. Called when a
 * simulation restarts, which is the one thing the old representation got wrong: it read the
 * state left by the wrapped-out end of the previous run instead.
 */
export function reset(ram) {
    const matchValue = ram.Addressing;
    switch (matchValue.tag) {
        case 1: {
            const root = matchValue.fields[0];
            fill_1(root, 0, root.length, undefined);
            break;
        }
        case 2: {
            matchValue.fields[0].contents = empty_1({
                Compare: (x, y) => (compare(x, y) | 0),
            });
            break;
        }
        case 3: {
            break;
        }
        default: {
            const slots = matchValue.fields[0];
            fill_1(slots, 0, slots.length, -1);
        }
    }
    if (ram.Words.length > 0) {
        fill_1(ram.Words, 0, ram.Words.length, 0);
    }
    if (ram.BigWords.length > 0) {
        fill_1(ram.BigWords, 0, ram.BigWords.length, fromZero());
    }
    ram.SlotCount = 0;
    ram.SlotAddr = (new Uint32Array(0));
    ram.SlotAddrBig = [];
    ram.CurVal = (new Uint32Array(0));
    ram.CurBig = (new Array(0));
    ram.Start = (new Int32Array(1));
    ram.IStep = (new Int32Array(0));
    ram.IVal = (new Uint32Array(0));
    ram.IBig = (new Array(0));
    ram.ILen = 0;
    ram.TSlot = (new Int32Array(0));
    ram.TStep = (new Int32Array(0));
    ram.TVal = (new Uint32Array(0));
    ram.TBig = (new Array(0));
    ram.TLen = 0;
    ram.LastStep = 0;
    ram.Writes = 0;
    if (ram.Addressing.tag === 3) {
    }
    else {
        const pastTheEnd = op_LeftShift(fromOne(), ram.AddressWidth);
        iterate((addr, value) => {
            if ((!equals(value, fromZero()) && (compare(addr, fromZero()) >= 0)) && (compare(addr, pastTheEnd) < 0)) {
                if (ram.BigVals) {
                    writeAddrBigIntDataBigInt(ram, -1, addr, value);
                }
                else {
                    writeAddrBigIntDataUInt32(ram, -1, addr, toU(value));
                }
            }
        }, ram.InitialData);
        ram.Writes = 0;
        ram.LastStep = 0;
    }
}

function emptyRam(addressWidth, wordWidth, init, comments, initialData, addressing, words, bigWords, window$) {
    return new Ram(addressWidth, wordWidth, wordWidth > 32, init, comments, initialData, addressing, window$, words, bigWords, 0, new Uint32Array(0), [], new Uint32Array(0), new Array(0), new Int32Array(1), new Int32Array(0), new Uint32Array(0), new Array(0), 0, new Int32Array(0), new Int32Array(0), new Uint32Array(0), new Array(0), 0, 0, 0);
}

/**
 * Build the store for one read/write memory. `window` is the simulation's `MaxArraySize`: the
 * number of past steps that can still be asked about.
 */
export function ofMemory(window$, mem) {
    const aw = mem.AddressWidth | 0;
    const big = mem.WordWidth > 32;
    let patternInput;
    if (aw <= Constants_maxDenseAddressWidth) {
        const n = (1 << aw) | 0;
        patternInput = [new Addressing(/* Dense */ 0, [fill_1(new Int32Array(n), 0, n, -1)]), big ? (new Uint32Array(0)) : (new Uint32Array(n)), big ? fill_1(new Array(n), 0, n, fromZero()) : (new Array(0))];
    }
    else {
        patternInput = ((aw <= Constants_maxTrieAddressWidth) ? [new Addressing(/* Sparse */ 1, [fill_1(new Array(Constants_trieBranch), 0, Constants_trieBranch, undefined), ~~((aw + 3) / 4)]), new Uint32Array(0), new Array(0)] : [new Addressing(/* Wide */ 2, [new FSharpRef(empty_1({
            Compare: (x, y) => (compare(x, y) | 0),
        }))]), new Uint32Array(0), new Array(0)]);
    }
    const ram = emptyRam(aw, mem.WordWidth, mem.Init, mem.Comments, mem.Data, patternInput[0], patternInput[1], patternInput[2], window$);
    reset(ram);
    return ram;
}

/**
 * A read-only memory wrapped so that the one extraction path can serve ROMs and RAMs alike.
 */
export function fixedOf(mem) {
    return emptyRam(mem.AddressWidth, mem.WordWidth, mem.Init, mem.Comments, mem.Data, Addressing.Fixed, new Uint32Array(0), new Array(0), 0);
}

